import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Moon, Sun, ExternalLink, Folder, Menu, X, Code2, Terminal, Gamepad2, MapPin, GraduationCap, PcCase, Dumbbell} from 'lucide-react';

const Portfolio = () => {
  const [darkMode, setDarkMode] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setLightboxImage(null);
    };
    
    if (lightboxImage) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [lightboxImage]);

  const projects = [
    {
      title: 'Fly Easy',
      description: 'Airport companion app with features such as realtime flight and airport tracking, digital wallet, hotel and restaurant bookings, and an AI event planner. The event planner is made with detailed classical constraint based AI algorithms, with our final algorithm matching up to Claude Sonnet\'s ability to solve hard constraints.',
      tags: ['React Native', 'Flask', 'Firebase', 'Classical AI', 'Node.js'],
      github: 'https://github.com/HaseebSarfraz/Fly-Easy',
      demo: null,
      image: '/FlyEasy.png',
      featured: true,
    },
    {
      title: 'Pathway AI',
      description: 'Course selection and course scheduling assistant that helps students create an optimized schedule and course list based on preferences. Uses data scraping for course info, and a ChromaDB embeddings model parsing user preferences and feeding these prompts into ChatGPT queries for results. This is a project for Deerhacks.',
      tags: ['Next.js', 'OpenAI', 'ChromaDB', 'Beautiful Soup'],
      github: 'https://github.com/49M/Pathway_App',
      demo: null,
      image: '/Pathway.png',
      featured: true,
    },
    {
      title: 'Dr. Mario',
      description: 'This is a Doctor Mario game programmed in Assembly. The project was done beautifully with efficient register allocation and a show of understanding towards machine code, and how its flow in a microprocessor.',
      tags: ['Assembly', 'Saturn IDE'],
      github: 'https://github.com/JigsawDragon/dr-mario-assembly',
      demo: null,
      image: '/Dr.Mario.png',
      featured: true,
    },
    {
      title: 'Task Tracker',
      description: 'Task Tracker with smooth transitions and practical UI for efficient Task Tracking capabilities.',
      tags: ['Angular', '.NET Core', 'SSMS (Microsoft SQL)'],
      github: 'https://github.com/JigsawDragon/task-tracker',
      demo: null,
      image: '/TaskTracker.png',
      featured: true,
    },
  ];

  const skills = {
    'Languages': ['Python', 'Java', 'C/C++', 'C#', 'HTML', 'CSS', 'Javascript/Typescript', 'Assembly', 'Racket', 'Haskell'],
    'Frameworks': ['Angular', 'React', 'React Native', 'Next.js', '.NET Core', 'EF Core', 'Flask', 'NumPy', 'Unity', 'Godot'],
    'Databases': ['MySQL','PostgreSQL', 'SSMS (Microsoft)'],
    'Developer Tools': ['Linux', 'Docker', 'Git', 'Node.js', 'Vim', 'Jira']
  };

  const featuredProjects = projects.filter(p => p.featured);

  return (
    <div className={darkMode ? 'dark' : ''}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Inter', sans-serif;
        }
        
        h1, h2, h3, h4, h5, h6, .font-mono {
          font-family: 'JetBrains Mono', monospace;
        }

        html {
          scroll-behavior: smooth;
        }

         /* Light Mode - Red Theme */
        :root {
          --bg: 255, 255, 255;
          --fg: 17, 24, 39;
          --primary: 220, 38, 38;
          --primary-rgb: 220, 38, 38;
          --accent: 239, 68, 68;
          --muted: 100, 116, 139;
          --border: 226, 232, 240;
          --card: 248, 250, 252;
        }

        /* Dark Mode - Red/Black Theme */
        .dark {
          --bg: 17, 24, 39;
          --fg: 243, 244, 246;
          --primary: 239, 68, 68;
          --primary-rgb: 239, 68, 68;
          --accent: 220, 38, 38;
          --muted: 107, 114, 128;
          --border: 31, 41, 55;
          --card: 31, 41, 55;
        }

        .portfolio-root {
          background-color: rgb(var(--bg));
          color: rgb(var(--fg));
          min-height: 100vh;
        }

        .text-primary { color: rgb(var(--primary)); }
        .bg-primary { background-color: rgb(var(--primary)); }
        .text-muted { color: rgb(var(--muted)); }
        .border-color { border-color: rgb(var(--border)); }
        .bg-card { background-color: rgb(var(--card)); }

        .text-gradient {
          background: linear-gradient(90deg, rgb(var(--primary)) 0%, rgb(var(--accent)) 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .grid-pattern {
          background-image: 
            linear-gradient(rgba(var(--primary-rgb), 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(var(--primary-rgb), 0.05) 1px, transparent 1px);
          background-size: 60px 60px;

        }

        .glow-primary-strong {
          box-shadow: 0 0 40px rgba(var(--primary-rgb), 0.4);
        }

        /* Lightbox styles */
        .lightbox-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.9);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .lightbox-image {
          max-width: 90vw;
          max-height: 90vh;
          object-fit: contain;
          cursor: default;
          animation: zoomIn 0.3s ease;
        }

        @keyframes zoomIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
          opacity: 0;
        }

        .animate-bounce {
          animation: bounce 1s infinite;
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(-25%);
            animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
          }
          50% {
            transform: translateY(0);
            animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
          }
        }

        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        .stagger-4 { animation-delay: 0.4s; }
        .stagger-5 { animation-delay: 0.5s; }

        .link-underline {
          position: relative;
        }

        .link-underline::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 2px;
          background: rgb(var(--primary));
          transition: width 0.3s ease;
        }

        .link-underline:hover::after {
          width: 100%;
        }

        .project-card, .skill-card, .interest-card {
          background: linear-gradient(145deg, rgb(var(--card)) 0%, rgba(var(--bg), 0.8) 100%);
          border: 1px solid rgb(var(--border));
          transition: all 0.3s ease;
        }

        .project-card:hover, .skill-card:hover, .interest-card:hover {
          box-shadow: 0 0 20px rgba(var(--primary-rgb), 0.3);
          border-color: rgb(var(--primary));
        }

        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
        }
        @media (min-width: 769px) {
          .mobile-nav { display: none !important; }
        }
      `}</style>

      <div className="portfolio-root">
        {/* Lightbox Modal */}
        {lightboxImage && (
          <div 
            className="lightbox-overlay" 
            onClick={() => setLightboxImage(null)}
          >
            <img 
              src={lightboxImage} 
              alt="Full size preview"
              className="lightbox-image"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Navigation */}
        <nav style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backgroundColor: isScrolled ? `rgba(var(--bg), 0.9)` : 'transparent',
          backdropFilter: isScrolled ? 'blur(12px)' : 'none',
          borderBottom: isScrolled ? `1px solid rgb(var(--border))` : 'none',
          transition: 'all 0.3s'
        }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '1rem 1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="font-mono text-primary"
                style={{ fontSize: '1.25rem', fontWeight: 'bold', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                {'<Siddharth Iyer/>'}
              </button>

              {/* Desktop Navigation */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }} className="desktop-nav">
                <a href="#about" className="font-mono text-muted link-underline" style={{ fontSize: '0.875rem', textDecoration: 'none' }}>About</a>
                <a href="#skills" className="font-mono text-muted link-underline" style={{ fontSize: '0.875rem', textDecoration: 'none' }}>Skills</a>
                <a href="#projects" className="font-mono text-muted link-underline" style={{ fontSize: '0.875rem', textDecoration: 'none' }}>Projects</a>
                <a href="#interests" className="font-mono text-muted link-underline" style={{ fontSize: '0.875rem', textDecoration: 'none' }}>Interests</a>
                <a href="#contact" className="font-mono text-muted link-underline" style={{ fontSize: '0.875rem', textDecoration: 'none' }}>Contact</a>
                <a href="https://github.com/JigsawDragon" target="_blank" rel="noopener noreferrer" className="text-muted" style={{ transition: 'color 0.3s' }}><Github size={24} /></a>
                <a href="https://www.linkedin.com/in/siyer2405/" target="_blank" rel="noopener noreferrer" className="text-muted" style={{ transition: 'color 0.3s' }}>
                  <Linkedin size={24} />
                </a>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className="bg-card border-color"
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    border: '1px solid',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                >
                  {darkMode ? <Sun className="text-primary" size={20} /> : <Moon className="text-primary" size={20} />}
                </button>
              </div>

              {/* Mobile Menu */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }} className="mobile-nav">
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className="bg-card border-color"
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    border: '1px solid',
                    cursor: 'pointer'
                  }}
                >
                  {darkMode ? <Sun className="text-primary" size={20} /> : <Moon className="text-primary" size={20} />}
                </button>
                <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            </div>

            {isMobileMenuOpen && (
              <div style={{ marginTop: '1rem', paddingBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <a href="#about" className="font-mono text-muted" style={{ fontSize: '0.875rem', textDecoration: 'none' }} onClick={() => setIsMobileMenuOpen(false)}>About</a>
                <a href="#skills" className="font-mono text-muted" style={{ fontSize: '0.875rem', textDecoration: 'none' }} onClick={() => setIsMobileMenuOpen(false)}>Skills</a>
                <a href="#projects" className="font-mono text-muted" style={{ fontSize: '0.875rem', textDecoration: 'none' }} onClick={() => setIsMobileMenuOpen(false)}>Projects</a>
                <a href="#interests" className="font-mono text-muted" style={{ fontSize: '0.875rem', textDecoration: 'none' }} onClick={() => setIsMobileMenuOpen(false)}>Interests</a>
                <a href="#contact" className="font-mono text-muted" style={{ fontSize: '0.875rem', textDecoration: 'none' }} onClick={() => setIsMobileMenuOpen(false)}>Contact</a>
                <a href="https://github.com/JigsawDragon" target="_blank" rel="noopener noreferrer" className="text-muted" style={{ transition: 'color 0.3s' }}><Github size={24} /></a>
                <a href="https://www.linkedin.com/in/siyer2405/" target="_blank" rel="noopener noreferrer" className="text-muted" style={{ transition: 'color 0.3s' }}><Linkedin size={24} /></a>
              </div>
            )}
          </div>
        </nav>

        {/* Hero Section */}
        <section style={{ position: 'relative', minHeight: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'}}>
          <div className="grid-pattern" style={{ position: 'absolute', inset: 0, opacity: 0.3 }} />
          
          <div className="animate-float" style={{ position: 'absolute', top: '25%', left: '-8rem', width: '16rem', height: '16rem', borderRadius: '9999px', filter: 'blur(80px)', backgroundColor: `rgba(var(--primary-rgb), 0.2)` }} />
          <div className="animate-float" style={{ position: 'absolute', bottom: '25%', right: '-8rem', width: '24rem', height: '24rem', borderRadius: '9999px', filter: 'blur(80px)', backgroundColor: `rgba(var(--primary-rgb), 0.1)`, animationDelay: '3s' }} />

          <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem', position: 'relative', zIndex: 10 }}>
            <div style={{ maxWidth: '56rem', margin: '0 auto', textAlign: 'center' }}>
              <div className="animate-fade-up">
                <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>
                  {"> Hello, World! I'm"}
                </span>
              </div>

              <h1 className="font-mono animate-fade-up stagger-1" style={{ marginTop: '1rem', fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', fontWeight: 'bold' }}>
                Siddharth Iyer
              </h1>

              <p className="font-mono animate-fade-up stagger-2" style={{ marginTop: '1.5rem', fontSize: 'clamp(1.25rem, 4vw, 2rem)' }}>
                <span className="text-gradient">Software Developer</span>
                <span className="text-muted"> & </span>
                <span className="text-gradient">CS Student</span>
              </p>

              <p className="text-muted animate-fade-up stagger-3" style={{ marginTop: '2rem', fontSize: '1.125rem', maxWidth: '42rem', margin: '2rem auto 0', lineHeight: '1.75' }}>
                4th year Computer Science student at University of Toronto passionate about building things 
                that matter.
              </p>

              <div className="animate-fade-up stagger-4" style={{ marginTop: '2.5rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
                <a
                  href="#projects"
                  className="bg-primary glow-primary-strong font-mono"
                  style={{
                    padding: '0.75rem 2rem',
                    color: 'white',
                    fontWeight: '500',
                    borderRadius: '0.375rem',
                    textDecoration: 'none',
                    transition: 'all 0.3s',
                    display: 'inline-block'
                  }}
                >
                  View My Work
                </a>
                <a
                  href="#contact"
                  className="font-mono"
                  style={{
                    padding: '0.75rem 2rem',
                    border: '1px solid rgb(var(--primary))',
                    fontWeight: '500',
                    borderRadius: '0.375rem',
                    textDecoration: 'none',
                    transition: 'all 0.3s',
                    display: 'inline-block',
                    color: 'rgb(var(--primary))'
                  }}
                >
                  Get In Touch
                </a>
              </div>

            </div>
          </div>

          {/* <a href="#about" className="text-muted animate-bounce" style={{ position: 'absolute', bottom: '2rem', left: '49%', transform: 'translateX(-50%)', transition: 'color 0.3s' }}>
            <ChevronDown size={32} />
          </a> */}
        </section>

        {/* About Section */}
        <section id="about" style={{ padding: '6rem 0', position: 'relative' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>{'// About Me'}</span>
              <h2 className="font-mono" style={{ marginTop: '0.5rem', fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold' }}>Who I Am</h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem', alignItems: 'start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <p className="text-muted" style={{ lineHeight: '1.75', fontSize: '1.125rem' }}>
                  I'm a fourth-year Computer Science student at the University of Toronto, 
                  driven by curiosity about how things work. Whether it's diving 
                  into optimizing algorithms, or crafting interactive 
                  game mechanics, I love the challenge of turning complex problems into 
                  elegant solutions.
                </p>
                <p className="text-muted" style={{ lineHeight: '1.75', fontSize: '1.125rem' }}>
                  My journey in software development started with a love for video games, and not just playing 
                  them, but understanding how they're built. This led me to explore game 
                  development, which naturally evolved into a broader passion for general software development,
                  with some focused interest in low-level programming and machine-learning.
                </p>
                <p className="text-muted" style={{ lineHeight: '1.75', fontSize: '1.125rem' }}>
                  When I'm not working towards my goals, you'll find me exploring new games, playing sports, 
                  and learning about something fun to add to my arsenal of skills.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', paddingTop: '1rem' }}>
                  <div className="border-color bg-card" style={{ padding: '1rem', borderRadius: '0.75rem', border: '1px solid' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <MapPin className="text-primary" size={20} />
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Location</span>
                    </div>
                    <p style={{ fontWeight: '500' }}>Toronto, ON</p>
                  </div>
                  <div className="border-color bg-card" style={{ padding: '1rem', borderRadius: '0.75rem', border: '1px solid' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <GraduationCap className="text-primary" size={20} />
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Education</span>
                    </div>
                    <p style={{ fontWeight: '500' }}>CS @ University of Toronto</p>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="interest-card" style={{ padding: '1.5rem', borderRadius: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: `rgba(var(--primary-rgb), 0.2)` }}>
                      <Code2 className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="font-mono" style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>Software Engineering</h3>
                      <p className="text-muted" style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                        Building robust, scalable applications with clean architecture and best 
                        practices. From web apps to CLI tools.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="interest-card" style={{ padding: '1.5rem', borderRadius: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: `rgba(var(--primary-rgb), 0.2)` }}>
                      <Gamepad2 className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="font-mono" style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>Game Development</h3>
                      <p className="text-muted" style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                        Creating interactive experiences and game mechanics. Passionate 
                        about both playing and making games.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="interest-card" style={{ padding: '1.5rem', borderRadius: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: `rgba(var(--primary-rgb), 0.2)` }}>
                      <Terminal className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="font-mono" style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>Systems Programming</h3>
                      <p className="text-muted" style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                        Exploring systems programming, memory management, and 
                        understanding how computers really work at the metal.
                      </p>
                    </div>
                  </div>
                </div>

                
              </div>
            </div>
          </div>
        </section>

        {/* Skills Section */}
        <section id="skills" style={{ padding: '6rem 0', position: 'relative' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>{'// Tech Stack'}</span>
              <h2 className="font-mono" style={{ marginTop: '0.5rem', fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold' }}>Technical Skills</h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              {Object.entries(skills).map(([category, techs]) => (
                <div key={category} className="skill-card" style={{ padding: '1.5rem', borderRadius: '0.75rem' }}>
                  <h3 className="font-mono text-primary" style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>{category}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {techs.map((tech) => (
                      <div key={tech} className="bg-card" style={{ padding: '0.5rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.875rem', transition: 'color 0.3s' }}>
                        {tech}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section id="projects" style={{ padding: '6rem 0', position: 'relative' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>{'// Portfolio'}</span>
              <h2 className="font-mono" style={{ marginTop: '0.5rem', fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold' }}>Featured Projects</h2>
              <p className="text-muted" style={{ marginTop: '1rem', maxWidth: '42rem', margin: '1rem auto 0', fontSize: '1.125rem' }}>
                A selection of projects I've worked on. Each one taught me something new.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginBottom: '4rem' }}>
              {featuredProjects.map((project, index) => (
                <div
                  key={project.title}
                  className="project-card"
                  style={{
                    padding: '2rem',
                    borderRadius: '0.75rem',
                    marginLeft: index % 2 === 1 ? '3rem' : '0',
                    marginRight: index % 2 === 0 ? '3rem' : '0'
                  }}
                >
                  <div style={{ display: 'flex', gap: '2rem', alignItems: 'start', flexWrap: 'wrap' }}>
                    {project.image && (
                      <div 
                        onClick={() => setLightboxImage(project.image)}
                        style={{ 
                          flex: '0 0 350px',
                          borderRadius: '0.5rem', 
                          overflow: 'hidden',
                          border: '1px solid rgb(var(--border))',
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'rgb(var(--card))',
                          cursor: 'pointer'
                        }}
                      >
                        <img 
                          src={project.image} 
                          alt={project.title}
                          style={{ 
                            width: '100%', 
                            height: 'auto',
                            maxHeight: '280px',
                            objectFit: 'contain',
                            display: 'block',
                            transition: 'transform 0.3s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
                          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        />
                      </div>
                    )}
                    
                    <div style={{ flex: 1, minWidth: '300px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                            <Folder className="text-primary" size={20} />
                            <h3 className="font-mono" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                              {project.title}
                            </h3>
                          </div>
                          <p className="text-muted" style={{ lineHeight: '1.75', marginBottom: '1rem' }}>
                            {project.description}
                          </p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {project.tags.map((tag) => (
                              <span key={tag} className="font-mono text-primary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: `rgba(var(--primary-rgb), 0.1)`, borderRadius: '0.25rem' }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          {project.github && (
                            <a href={project.github} target="_blank" rel="noopener noreferrer" className="text-muted" style={{ transition: 'color 0.3s' }}>
                              <Github size={22} />
                            </a>
                          )}
                          {project.demo && (
                            <a href={project.demo} target="_blank" rel="noopener noreferrer" className="text-muted" style={{ transition: 'color 0.3s' }}>
                              <ExternalLink size={22} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Interests Section */}
        <section id="interests" style={{ padding: '6rem 0', position: 'relative' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>{'// Beyond Code'}</span>
              <h2 className="font-mono" style={{ marginTop: '0.5rem', fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold' }}>Interests & Hobbies</h2>
              <p className="text-muted" style={{ marginTop: '1rem', maxWidth: '42rem', margin: '1rem auto 0', fontSize: '1.125rem' }}>
                Things I'm passionate about outside of software development.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>
              {/* PC Build */}
              <div className="interest-card" style={{ padding: '2rem', borderRadius: '0.75rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ padding: '1rem', borderRadius: '0.75rem', backgroundColor: `rgba(var(--primary-rgb), 0.2)` }}>
                    <PcCase className="text-primary" size={32} />
                  </div>
                  <h3 className="font-mono" style={{ fontSize: '1.5rem', fontWeight: '600' }}>PC Building</h3>
                </div>
                
                <p className="text-muted" style={{ lineHeight: '1.75' }}>
                  I love building and optimizing PCs. After selecting the perfect components through weeks of research there's something satisfying about assembling and optimizing a machine from scratch. 
                  These are the specs for my current setup:
                </p>
                <div>
                  <h4 className="font-mono text-primary" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                      Parts
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>CPU:</span>
                      <span style={{ marginLeft: '0.5rem' }}>AMD Ryzen 7 9800X3d - 4.7GHz, 8 Cores</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>CPU Cooler:</span>
                      <span style={{ marginLeft: '0.5rem' }}>Thermalright Phantom Spirit 120 SE ARGB</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Motherboard:</span>
                      <span style={{ marginLeft: '0.5rem' }}>Gigabyte AORUS ELITE X870E - ATX, AM5, WIFI7</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Memory/RAM:</span>
                      <span style={{ marginLeft: '0.5rem' }}>Teamgroup T-Force Vulcan - 2×16GB DDR5</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Storage:</span>
                      <span style={{ marginLeft: '0.5rem' }}>Samsung 990 Pro 4TB - NVMe SSD, PCIe 4.0</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>GPU:</span>
                      <span style={{ marginLeft: '0.5rem' }}>Gigabyte Gaming OC Geforce RTX 5070 Ti - 16GB GDDR7</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Case:</span>
                      <span style={{ marginLeft: '0.5rem' }}>Fractal Design Pop Air RGB - ATX Mid Tower</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Power Supply/PSU:</span>
                      <span style={{ marginLeft: '0.5rem' }}>MSI MAG A1000GL 1000W - 80+ Gold Certified, Modular, PCIe 5</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-mono text-primary" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                      Peripherals
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                        <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Mouse:</span>
                        <span style={{ marginLeft: '0.5rem' }}>Logitech G502 Wireless</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                        <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Keyboard:</span>
                        <span style={{ marginLeft: '0.5rem' }}>MCHOSE Jet 75 - Hall Effect, Kailh God Switches</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                        <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Monitors:</span>
                        <span style={{ marginLeft: '0.5rem' }}>HP E24 G4 60Hz 1920x1080p, Samsung Odyssey G5 180Hz 2460x1440p</span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>
                {/* Fitness */}
                <div className="interest-card" style={{ padding: '2rem', borderRadius: '0.75rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ padding: '1rem', borderRadius: '0.75rem', backgroundColor: `rgba(var(--primary-rgb), 0.2)` }}>
                      <Dumbbell className="text-primary" size={32} />
                    </div>
                    <h3 className="font-mono" style={{ fontSize: '1.5rem', fontWeight: '600' }}>Fitness</h3>
                  </div>
                  
                  <p className="text-muted" style={{ lineHeight: '1.75' }}>
                    I consider fitness in sports and bodybuilding to be the key to my long-term health, and I find it fun at the same time. I like keeping track of my numbers in the gym and cardio-wise, and I also enjoy playing several sports.
                  </p>

                  <div>
                    <h4 className="font-mono text-primary" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                      Gym & Cardio (Peak)
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Bench Press</span>
                        <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>155 lbs</span>
                      </div>
                      <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Squat</span>
                        <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>225 lbs</span>
                      </div>
                      <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Deadlift</span>
                        <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>225 lbs</span>
                      </div>
                      <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Run (5k)</span>
                        <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>37:31</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-mono text-primary" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                        Sports of Interest
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                          <span style={{ marginLeft: '0.5rem' }}>Badminton</span>
                      </div>
                      <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                          <span style={{ marginLeft: '0.5rem' }}>Table Tennis (Ping Pong)</span>
                      </div>
                      <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                          <span style={{ marginLeft: '0.5rem' }}>Basketball</span>
                      </div>
                      <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                          <span style={{ marginLeft: '0.5rem' }}>Swimming</span>
                      </div>
                      <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                          <span style={{ marginLeft: '0.5rem' }}>Football (Soccer)</span>
                      </div>
                      <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                          <span style={{ marginLeft: '0.5rem' }}>Cricket</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Games */}
              <div className="interest-card" style={{ padding: '2rem', borderRadius: '0.75rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ padding: '1rem', borderRadius: '0.75rem', backgroundColor: `rgba(var(--primary-rgb), 0.2)` }}>
                    <Gamepad2 className="text-primary" size={32} />
                  </div>
                  <h3 className="font-mono" style={{ fontSize: '1.5rem', fontWeight: '600' }}>Games</h3>
                </div>
                
                <p className="text-muted" style={{ lineHeight: '1.75' }}>
                  Gaming represents an essential attribute of humanity: Storytelling. Through games, we experience narratives, challenges, and worlds beyond our own which have been conceived from individual imaginations. I love games. 
                </p>

                {/* Currently Playing */}
                <div>
                  <h4 className="font-mono text-primary" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                    Currently Playing
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Hollow Knight: Silksong</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Balatro</span>
                    </div>
                  </div>
                </div>

                {/* Competitive Ranks (Peak)*/}
                <div>
                  <h4 className="font-mono text-primary" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                    Competitive Ranks (Peak)
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Clash Royale</span>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Champion</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Valorant</span>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Platinum 1</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>League of Legends</span>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Bronze 4</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Chess</span>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Rapid: 1000</span>
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>MonkeyType</span>
                      <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>30 Seconds: 133wpm </span>
                    </div>
                  </div>
                </div>

                {/* Completed Games */}
                <div>
                  <h4 className="font-mono text-primary" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                      100% Completed Games
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                        <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Yakuza Kiwami</span> 
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                        <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Hollow Knight</span> 
                    </div>
                    <div className="bg-card" style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem' }}>
                        <span className="font-mono text-primary" style={{ fontSize: '0.875rem' }}>Ghost of Tsushima</span> 
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" style={{ padding: '1rem 0', position: 'relative' }}>
          <div style={{ maxWidth: '56rem', margin: '0 auto', padding: '0 1.5rem', textAlign: 'center' }}>
            <span className="font-mono text-primary" style={{ fontSize: '0.875rem'}}>{'// Get In Touch'}</span>
            <h2 className="font-mono" style={{ marginTop: '0.5rem', fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold', marginBottom: '1.5rem' }}>Email: siyer2405@gmail.com</h2>
            <h2 className="font-mono" style={{ marginTop: '0.5rem', fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold', marginBottom: '1.5rem' }}><a href='https://www.instagram.com/jigsaw_dragon/' style={{ color: '#60a5fa' }}>Insta: @jigsaw_dragon</a></h2>
            <p className="text-muted" style={{ fontSize: '1.125rem', marginBottom: '2rem', maxWidth: '42rem', margin: '0 auto 2rem', lineHeight: '1.75' }}>
              I'm always open to discussing new opportunities, projects, or just chatting about tech.
              Feel free to reach out at my email above.
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-color" style={{ padding: '2rem 1.5rem', borderTop: '1px solid' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <p className="text-muted font-mono" style={{ fontSize: '0.875rem' }}>
              © 2026 Siddharth Iyer. Built with React.
            </p>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <a href="https://github.com/JigsawDragon" target="_blank" rel="noopener noreferrer" className="text-muted font-mono" style={{ fontSize: '0.875rem', textDecoration: 'none', transition: 'color 0.3s' }}>
                GitHub
              </a>
              <a href="https://www.linkedin.com/in/siyer2405/" target="_blank" rel="noopener noreferrer" className="text-muted font-mono" style={{ fontSize: '0.875rem', textDecoration: 'none', transition: 'color 0.3s' }}>
                LinkedIn
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Portfolio;
